import {AfterViewInit, Component, OnDestroy, OnInit, ViewEncapsulation} from '@angular/core';
import {fromEvent, Subscription} from "rxjs";
import {debounceTime} from "rxjs/operators";
import {FullTextSearchConfigurationModel} from "../../core/models/full-text-search/full-text-search-configuration.model";
import {FullTextSearchResultsModel} from "../../core/models/full-text-search/full-text-search-results.model";
import {ActivatedRoute, Router} from "@angular/router";

@Component({
  selector: 'sb-search-page',
  templateUrl: './search-page.component.html',
  styleUrls: ['./search-page.component.scss'],
})
export class SearchPageComponent implements OnInit, OnDestroy, AfterViewInit {

  searchText: string = "";

  lastSearch: string;

  lastSearchTrimmed: string;

  showTrigramWarning: boolean = false;

  searchResults: FullTextSearchResultsModel[] = [];

  private debounceSubscription: Subscription | null;

  constructor(private activatedRoute: ActivatedRoute) { }

  async ngOnInit() {
    // Wipe out the Knockout content.
    const pageContentMain = document.getElementById("page-content-main");
    if (pageContentMain) {
      pageContentMain.innerHTML = "";
    }

    this.activatedRoute.queryParams.subscribe((q) => {
      const text = q["searchText"];
      if (text) {
        this.searchText = text;
        this.search();
      }
    });
  }

  ngOnDestroy(): void {
    ( this.debounceSubscription ) && this.debounceSubscription.unsubscribe();
  }

  ngAfterViewInit(): void {
    this.debounceSubscription = fromEvent(document.getElementById('fullTextSearch'), 'keyup').pipe(
      debounceTime(500)
    ).subscribe(data => this.search());
  }

  evaluateTrigramWarning(): void {
    this.showTrigramWarning = false;

    this.searchText.split(" ")
      .filter(it => it.length > 0)
      .forEach(it => {
        if (it.length < 3) {
          this.showTrigramWarning = true;
        }
      });
  }

  trimToSearchableString(input: string): string {
    return this.searchText.split(" ")
      .filter(it => it.length >= 3)
      .join(" ");
  };

  evaluateShouldPerformSearch(): boolean {
    return this.searchText.split(" ").filter(it => it.length >= 3).length > 0;
  }

  async search() {
    const searchText = (this.searchText == null) ? "" : this.searchText.trim();
    const searchableText = this.trimToSearchableString(searchText);

    if (searchText === this.lastSearch?.trim()) {
      this.lastSearch = searchText;
      this.lastSearchTrimmed = this.trimToSearchableString(searchText);
      return;
    }

    if (searchableText === this.lastSearchTrimmed) {
      this.lastSearch = searchText;
      this.lastSearchTrimmed = searchableText;
      return;
    }

    this.lastSearch = searchText;
    this.lastSearchTrimmed = this.trimToSearchableString(searchText);

    if (searchText === "") {
      this.showTrigramWarning = false;
      return;
    }

    this.evaluateTrigramWarning();

    if (!this.evaluateShouldPerformSearch()) {
      return;
    }

    this.searchResults = [];

    const configResponse = await (window as any).WS.controller("FullTextSearch", {
        request_type: "default_search_config",
      }
    );

    const configs: FullTextSearchConfigurationModel[] = configResponse.data;

    configs.forEach((c) => {
      c.knockout = c.template?.includes("data-bind");
      c.default = c.template === undefined || c.template === null || c.template.length == 0;
    });

    const searchResults: FullTextSearchResultsModel[] = [];

    for (let i = 0; i < configs.length; ++i) {
      try {
        const itemResponse = await (window as any).WS.controller("FullTextSearch", {
            request_type: "search",
            text: this.searchText,
            search_type: configs[i].id
          }
        );
        if (itemResponse._error) {
          console.error("SearchPageComponent.search", itemResponse._error);
        } else {
          if (itemResponse.data.length > 0) {
            searchResults.push({config: configs[i], results: itemResponse.data});
          }
        }
      } catch (e) {
        console.error(e);
      }
    }

    // The 10000 is for missing order values.
    searchResults.sort((lhs, rhs): number => {
      return (lhs.config.order || 10000) - (rhs.config.order || 10000);
    });

    this.searchResults = searchResults;
  }

  clearSearch() {
    this.searchText = "";
    this.searchResults = [];
  }
}
